import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Progress,
  VStack,
  HStack,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getUsefulVars } from '../../handlers/helpers';
import { onLink } from '../../handlers';
import { handleAptitudeResults } from './utils';

const HollandCodeTest = ({ props,onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [scores, setScores] = useState({ R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 });
  const [showResults, setShowResults] = useState(false);

  const questions = [
    { text: "I enjoy working with tools and machinery", type: "R" },
    { text: "I like solving complex problems and puzzles", type: "I" },
    { text: "I enjoy creative activities like art, music, or writing", type: "A" },
    { text: "I like helping people and working in teams", type: "S" },
    { text: "I enjoy leading projects and persuading others", type: "E" },
    { text: "I prefer organized, structured tasks with clear procedures", type: "C" },
    { text: "I like hands-on work and building things", type: "R" },
    { text: "I enjoy conducting research and analyzing data", type: "I" },
    { text: "I like expressing myself through creative mediums", type: "A" },
    { text: "I enjoy teaching or counseling others", type: "S" },
    { text: "I like managing people and making business decisions", type: "E" },
    { text: "I prefer working with numbers and detailed records", type: "C" },
    { text: "I enjoy outdoor work and physical activities", type: "R" },
    { text: "I like exploring scientific or mathematical concepts", type: "I" },
    { text: "I enjoy performing or designing", type: "A" },
    { text: "I like volunteering and community service", type: "S" },
    { text: "I enjoy sales and entrepreneurial ventures", type: "E" },
    { text: "I prefer following established procedures and systems", type: "C" },
  ];

  const typeDescriptions = {
    R: {
      name: "Realistic",
      description: "Prefers hands-on work with tools, machines, or animals. Values practical tasks.",
      careers: "Engineer, Mechanic, Carpenter, Electrician, Pilot"
    },
    I: {
      name: "Investigative",
      description: "Enjoys analyzing, researching, and solving complex problems. Values knowledge.",
      careers: "Scientist, Researcher, Doctor, Analyst, Programmer"
    },
    A: {
      name: "Artistic",
      description: "Values creativity, self-expression, and originality. Prefers unstructured work.",
      careers: "Artist, Writer, Designer, Musician, Actor"
    },
    S: {
      name: "Social",
      description: "Enjoys helping, teaching, and working with people. Values relationships.",
      careers: "Teacher, Counselor, Nurse, Social Worker, Therapist"
    },
    E: {
      name: "Enterprising",
      description: "Enjoys leading, persuading, and managing. Values achievement and status.",
      careers: "Manager, Entrepreneur, Lawyer, Sales Rep, Marketing Director"
    },
    C: {
      name: "Conventional",
      description: "Prefers organized, structured work with clear rules. Values accuracy and efficiency.",
      careers: "Accountant, Banker, Data Analyst, Administrator, Auditor"
    }
  };

  const handleAnswer = (rating) => {
    const questionType = questions[currentQuestion].type;
    setScores(prev => ({
      ...prev,
      [questionType]: prev[questionType] + rating
    }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const getTopTypes = () => {
    const sortedScores = Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);
    return sortedScores;
  };

  const handleSaveAndContinue = (props) => {
    const topTypes = getTopTypes();
    const hollandCode = topTypes.map(([type]) => type).join('');
    const uv = getUsefulVars(props);

    const resume = uv.stateProps.dataToSave.saved.resumes[uv.resumeIndex];
    if(resume['new-aptitude'] === undefined) {
      resume['new-aptitude'] = [];
      resume['new-aptitude'].push({});
    }
    uv.stateProps.dataToSave.saved.resumes[uv.resumeIndex]['new-aptitude'][0].holland_code = hollandCode;

      onLink(false, props.formikProps, props.stateProps, 'new-aptitude',0,'section',props.stateProps.sectionData.resumeIndex);

  };

  const chartData = Object.entries(scores).map(([type, score]) => ({
    type: typeDescriptions[type].name,
    score: score,
    code: type
  }));

  const colors = {
    R: '#252f59',
    I: '#dd1198',
    A: '#ff9800',
    S: '#252f59',
    E: '#dd1198',
    C: '#ff9800'
  };

  if (showResults) {
    const topTypes = getTopTypes();
    const hollandCode = topTypes.map(([type]) => type).join('');

    return (
      <Container maxW="container.lg" py={6}>
        <Box bg="white" p={6} borderRadius="lg" shadow="lg">
          <Heading as="h2" size="xl" textAlign="center" mb={6} color="#252f59">
            Your Holland Code Results
          </Heading>

          <Box
            borderLeftWidth="4px"
            borderColor="#dd1198"
            bg="#fff5f7"
            p={4}
            mb={6}
            borderRadius="md"
          >
            <Text fontSize="lg" fontWeight="semibold" color="#252f59">
              Your Holland Code: <Text as="span" fontSize="2xl" color="#dd1198">{hollandCode}</Text>
            </Text>
          </Box>

          <Box mb={8}>
            <Heading as="h3" size="lg" mb={4} color="#252f59">
              Score Distribution
            </Heading>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[entry.code]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <VStack spacing={4} align="stretch">
            <Heading as="h3" size="lg" color="#252f59">
              Understanding Your Code
            </Heading>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Your Holland Code <Text as="strong" color="#dd1198">{hollandCode}</Text> represents a combination of your top three personality traits. Here's what each letter means:
            </Text>
            {topTypes.map(([type, score]) => (
              <Box
                key={type}
                borderLeftWidth="4px"
                borderColor={colors[type]}
                bg={`${colors[type]}10`}
                p={4}
                borderRadius="md"
              >
                <Heading as="h4" size="md" mb={2} color="#252f59">
                  {typeDescriptions[type].name} ({type}) - Score: {score}
                </Heading>
                <Text color="gray.700" mb={2}>
                  {typeDescriptions[type].description}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  <Text as="strong">Related Careers:</Text> {typeDescriptions[type].careers}
                </Text>
              </Box>
            ))}
          </VStack>

          {(() => {
            const sortedScores = Object.entries(scores).sort(([, a], [, b]) => b - a);
            const hasCloseScores = sortedScores.length >= 4 &&
              (sortedScores[2][1] === sortedScores[3][1] ||
                Math.abs(sortedScores[2][1] - sortedScores[3][1]) <= 3);

            if (hasCloseScores) {
              const alternativeCodes = sortedScores.slice(0, 4).map(([type]) => type);
              const alternativeCode = alternativeCodes[0] + alternativeCodes[1] + alternativeCodes[3];

              return (
                <Alert
                  status="warning"
                  variant="left-accent"
                  borderRadius="lg"
                  mt={6}
                  bg="#fff9e6"
                  borderColor="#ff9800"
                  borderWidth="2px"
                >
                  <AlertIcon />
                  <AlertDescription fontSize="sm" color="#252f59">
                    <Text as="strong">💡 Close Scores Detected:</Text> Your scores for some personality types are very similar.
                    When researching careers, you may also want to explore job matches for alternative codes like{' '}
                    <Text as="strong" color="#ff9800">{alternativeCode}</Text> or other combinations of your top types.
                  </AlertDescription>
                </Alert>
              );
            }
            return null;
          })()}

          <Button
            onClick={() => {
              handleSaveAndContinue(props);
            }}
            w="full"
            mt={6}
            bg="#dd1198"
            color="white"
            py={6}
            fontSize="md"
            width="260px"
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}

          >
            Save Results & Continue
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={6}>
      <Box bg="white" p={6} borderRadius="lg" shadow="lg">
        <Heading as="h1" size="xl" textAlign="center" mb={2} color="#252f59">
          Holland Code Career Test
        </Heading>
        <Text textAlign="center" color="gray.600" mb={6}>
          Discover your career personality type. Think about the skills you'd like to use at work and answer the questions below.
        </Text>

        <Box mb={6}>
          <Flex justify="space-between" fontSize="sm" color="gray.600" mb={2}>
            <Text>Question {currentQuestion + 1} of {questions.length}</Text>
            <Text>{Math.round(((currentQuestion) / questions.length) * 100)}%</Text>
          </Flex>
          <Progress
            value={((currentQuestion) / questions.length) * 100}
            size="sm"
            colorScheme="pink"
            bg="gray.200"
            borderRadius="full"
          />
        </Box>

        <Flex
          bg="gray.50"
          p={6}
          borderRadius="lg"
          mb={6}
          minH="32"
          align="center"
          justify="center"
        >
          <Text fontSize="xl" textAlign="center" fontWeight="semibold" color="#252f59">
            {questions[currentQuestion].text}
          </Text>
        </Flex>

        <VStack spacing={3}>
          <Button
            onClick={() => handleAnswer(5)}
            w="full"
            bg="#ff9800"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Strongly Agree
          </Button>
          <Button
            onClick={() => handleAnswer(3)}
            w="full"
            bg="#dd1198"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Agree
          </Button>
          <Button
            onClick={() => handleAnswer(1)}
            w="full"
            bg="#252f59"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Neutral
          </Button>
          <Button
            onClick={() => handleAnswer(-1)}
            w="full"
            bg="#6c757d"
            color="white"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Disagree
          </Button>
          <Button
            onClick={() => handleAnswer(-3)}
            w="full"
            bg="#e9ecef"
            color="#252f59"
            width="160px"
            py={6}
            fontWeight="semibold"
            borderRadius="lg"
            _hover={{ opacity: 0.9 }}
          >
            Strongly Disagree
          </Button>
        </VStack>
      </Box>
      <Button
        w="full"
        mt={6}
        bg="#dd1198"
        color="white"
        py={6}
        fontSize="md"
        width="260px"
        fontWeight="semibold"
        borderRadius="lg"
        _hover={{ opacity: 0.9 }}  className="aptitudeButton"
              onClick={() => onLink(false,props.formikProps,props.stateProps,'new-aptitude')}>
        Return
      </Button>
    </Container>
  );
};

export default HollandCodeTest;